import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle } from "src/core/redux";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import {
    Box,
    Button,
    Header,
    Link,
    Pagination,
    SpaceBetween,
    StatusIndicator,
    Table,
    TextFilter,
    Alert,
} from "@cloudscape-design/components";
import { useParams, useNavigate } from "react-router-dom";
import { getCodeReviewsByTeam } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { CodeReview, CodeReviewListResponse } from "src/core/sentinel/types";
import { SentinelPageProps } from "src/web/interfaces";

export interface CRDashboardPageProps extends SentinelPageProps {
  sentinelController: SentinelController;
}

export const CRDashboardPage: React.FC<CRDashboardPageProps> = (props: CRDashboardPageProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { teamName } = useParams<{ teamName: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [codeReviews, setCodeReviews] = useState<CodeReview[]>([]);
    const [filteringText, setFilteringText] = useState("");
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const pageSize = 50;

    useEffect(() => {
        dispatch(setPageTitle("CR Dashboard"));
    }, []);

    useEffect(() => {
        if (!teamName) {
            setLoading(false);
            return;
        }
        const fetchCRs = async () => {
            setError("");
            try {
                const response = await dispatch(
                    getCodeReviewsByTeam({
                        sentinelController: props.sentinelController,
                        teamName: teamName,
                    })
                ).unwrap();
                const crList = (response as any)?.payload?.codeReviewList;
                setCodeReviews(crList || []);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                setError("Failed to load code reviews.");
            }
        };
        fetchCRs();
    }, [teamName]);

    const filteredCRs = codeReviews.filter((cr) => {
        const content = [cr.codeReviewId, cr.packageName, cr.dependencyName, cr.status]
            .join(" ")
            .toLowerCase();
        return !filteringText || content.includes(filteringText.toLowerCase());
    });

    const paginatedItems = filteredCRs.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );

    const totalPages = Math.ceil(filteredCRs.length / pageSize);

    const getStatusIndicator = (status: string) => {
        switch (status?.toUpperCase()) {
        case "OPEN":
            return <StatusIndicator type="in-progress">{status}</StatusIndicator>;
        case "MERGED":
            return <StatusIndicator type="success">{status}</StatusIndicator>;
        case "CLOSED":
            return <StatusIndicator type="stopped">{status}</StatusIndicator>;
        case "PERMANENT_FAILURE":
            return <StatusIndicator type="error">{status}</StatusIndicator>;
        default:
            return <StatusIndicator type="info">{status}</StatusIndicator>;
        }
    };

    if (!teamName) {
        return (
            <SentinelBasePage>
                <Box textAlign="center" padding="xl">
                    <StatusIndicator type="error">
                        No team specified. Please navigate from the Teams page.
                    </StatusIndicator>
                </Box>
            </SentinelBasePage>
        );
    }

    return (
        <SentinelBasePage>
            {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
            <Table
                items={paginatedItems}
                columnDefinitions={[
                    {
                        id: "codeReviewId",
                        header: "Code Review",
                        cell: (e: CodeReview) => (
                            <Link href={`https://code.amazon.com/reviews/${e.codeReviewId}`} external>
                                {e.codeReviewId}
                            </Link>
                        ),
                        sortingField: "codeReviewId",
                        isRowHeader: true,
                    },
                    {
                        id: "packageName",
                        header: "Package",
                        cell: (e: CodeReview) => e.packageName,
                        sortingField: "packageName",
                    },
                    {
                        id: "dependencyName",
                        header: "Dependency",
                        cell: (e: CodeReview) => e.dependencyName,
                        sortingField: "dependencyName",
                    },
                    {
                        id: "upgrade",
                        header: "Upgrade",
                        cell: (e: CodeReview) => `${e.upgradeVersionFrom} → ${e.upgradeVersionTo}`,
                    },
                    {
                        id: "status",
                        header: "Status",
                        cell: (e: CodeReview) => getStatusIndicator(e.status),
                        sortingField: "status",
                    },
                    {
                        id: "createdAt",
                        header: "Created",
                        cell: (e: CodeReview) => e.createdAt,
                        sortingField: "createdAt",
                    },
                ]}
                renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                    `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
                }
                loading={loading}
                loadingText="Loading code reviews"
                empty={
                    <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                        <SpaceBetween size="m">
                            <b>No code reviews found</b>
                        </SpaceBetween>
                    </Box>
                }
                filter={
                    <TextFilter
                        filteringPlaceholder="Filter code reviews"
                        filteringText={filteringText}
                        onChange={({ detail }) => {
                            setFilteringText(detail.filteringText);
                            setCurrentPageIndex(1);
                        }}
                        countText={`${filteredCRs.length} matches`}
                    />
                }
                header={
                    <Header
                        counter={`(${codeReviews.length})`}
                        actions={
                            <Button variant="link" onClick={() => navigate("/team")}>
                ← Back to Onboarded Teams
                            </Button>
                        }
                    >
            Code Reviews — {teamName}
                    </Header>
                }
                pagination={
                    <Pagination
                        currentPageIndex={currentPageIndex}
                        pagesCount={totalPages}
                        onChange={({ detail }) =>
                            setCurrentPageIndex(detail.currentPageIndex)
                        }
                    />
                }
            />
        </SentinelBasePage>
    );
};
